import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, X } from 'lucide-react';
import { Button, FormInput, FormSelect } from '../ui';
import type { CampaignFollowFilters } from '../../types/campaignFollow';
import { campaignService } from '../../services/campaign.service';

interface CampaignFollowFiltersProps {
  filters: CampaignFollowFilters;
  onFilterChange: (filters: CampaignFollowFilters) => void;
  onClearFilters: () => void;
}

export default function CampaignFollowFiltersComponent({
  filters,
  onFilterChange,
  onClearFilters,
}: CampaignFollowFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);

  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns-list'],
    queryFn: () => campaignService.getAll(1, 1000),
  });

  const campaigns = campaignsData?.data || [];

  const handleFilterChange = (key: keyof CampaignFollowFilters, value: any) => {
    onFilterChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined && v !== '');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          Filtros
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
              {Object.values(filters).filter(v => v !== undefined && v !== '').length}
            </span>
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={onClearFilters}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Limpar Filtros
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t dark:border-gray-700">
          <FormSelect
            label="Campanha"
            name="campaign_id"
            value={filters.campaign_id?.toString() || ''}
            onChange={(e) => handleFilterChange('campaign_id', parseInt(e.target.value) || undefined)}
          >
            <option value="">Todas as campanhas</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </FormSelect>

          <FormInput
            label="Estágio Atual"
            name="stage"
            value={filters.stage || ''}
            onChange={(e) => handleFilterChange('stage', e.target.value)}
            placeholder="Filtrar por estágio"
          />

          <FormInput
            label="Novo Estágio"
            name="new_stage"
            value={filters.new_stage || ''}
            onChange={(e) => handleFilterChange('new_stage', e.target.value)}
            placeholder="Filtrar por novo estágio"
          />

          <FormInput
            label="Nova Ação"
            name="new_action"
            value={filters.new_action || ''}
            onChange={(e) => handleFilterChange('new_action', e.target.value)}
            placeholder="Filtrar por ação"
          />

          <FormSelect
            label="Status"
            name="is_active"
            value={filters.is_active !== undefined ? filters.is_active.toString() : ''}
            onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
          >
            <option value="">Todos os status</option>
            <option value="true">Ativo</option>
            <option value="false">Inativo</option>
          </FormSelect>
        </div>
      )}
    </div>
  );
}
